import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./useAuth";

export interface OrderRecord {
  id: string;
  user_id: string;
  product_id: string | null;
  amount: number;
  status: "active" | "completed" | "進行中";
  created_at: string;
  product_name: string | null;
  return_rate: number | null;
  profit: number | null;
  extra_profit: number | null;
  selling_amount: number | null;
  completion_percentage: number | null;
  quantity: number | null;
}

export function useOrders() {
  const { user, isLoggedIn } = useAuth();
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const userId = user?.id;
  const prevUserIdRef = useRef<string | null>(null);
  const prevRefreshKeyRef = useRef(0);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn || !userId) {
      setOrders([]);
      setLoading(false);
      prevUserIdRef.current = null;
      return;
    }

    if (userId === prevUserIdRef.current && refreshKey === prevRefreshKeyRef.current) {
      return;
    }
    prevUserIdRef.current = userId;
    prevRefreshKeyRef.current = refreshKey;

    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError("");

    const controller = new AbortController();

    const load = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from("orders")
          .select("id, user_id, product_id, amount, status, created_at, product_name, return_rate, profit, extra_profit, selling_amount, completion_percentage, quantity")
          .eq("user_id", userId)
          .order("created_at", { ascending: false });

        if (controller.signal.aborted) return;

        if (fetchError) {
          console.error("[useOrders] fetchOrders error:", fetchError.code, fetchError.message);
          setError("資料載入失敗");
          setOrders([]);
        } else {
          setOrders((data as OrderRecord[]) ?? []);
          setError("");
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("[useOrders] fetchOrders exception:", err);
        setError("資料載入失敗");
        setOrders([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          fetchingRef.current = false;
        }
      }
    };

    load();

    return () => {
      controller.abort();
    };
  }, [userId, isLoggedIn, refreshKey]);

  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const handleRefresh = () => refetch();
    window.addEventListener("orders:refresh", handleRefresh);
    return () => window.removeEventListener("orders:refresh", handleRefresh);
  }, [refetch]);

  // Supabase Realtime：後台一更新，前台立刻自動刷新
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`orders_${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders", filter: `user_id=eq.${userId}` },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, refetch]);

  return { orders, loading, error, refetch };
}